import React, { useMemo, useState, useEffect } from "react";
import { useGet } from "../../hooks/useGet";
import { getFromHubNew } from "../../actions/networkActions";
import { isDateTodayOrFuture } from "../../utils/dateTimeUtils";
import WeatherForecastInformation from "../../components/WeatherForecastInformation";
import FullScreenLoader from "../../components/FullScreenLoader";
import PropTypes from "prop-types";
import styled from "styled-components";

const PageWrapper = styled.div`
  background-color: ${props => props.theme.bodyBackground || "#F3F3F3"};
  color: ${props => props.theme.bodyColor || "#5a5a5a"};
`;

const ThemedHeading = styled.h1`
  color: ${props => props.theme.headerColor || "#6B207D"};
`;

const MAX_FORECAST_DAYS = 6;

const WeatherForecastOverview = (props, context) => {
  
  const { isLoading: loadingSchedules, error: schedulesError, data: dates } = useGet("itinerary/schedules");
  
  const activeDates = useMemo(() => {
    return dates?.filter(date => isDateTodayOrFuture(date.date)) || [];
  }, [dates]);

  const forecastableDates = useMemo(() => {
    return activeDates.slice(0, MAX_FORECAST_DAYS);
  }, [activeDates]);

  const [forecasts, setForecasts] = useState({});
  const [loadingForecasts, setLoadingForecasts] = useState(false);
  const [forecastError, setForecastError] = useState(null);

  useEffect(() => {
    if (forecastableDates.length === 0) return;

    const fetchAllForecasts = async () => {
      setLoadingForecasts(true);
      setForecastError(null);
      try {
        const forecastPromises = forecastableDates.map(async (day) => {
          try {
            const data = await getFromHubNew(`itinerary/weather/forecast/schedules/${day.id}`);
            return { id: day.id, data };
          } catch (err) {
            console.error(`Failed to fetch forecast for schedule ${day.id}`, err);
            return { id: day.id, data: null };
          }
        });

        const forecastResults = await Promise.all(forecastPromises);

        const forecastsMap = {};
        forecastResults.forEach(res => {
          forecastsMap[res.id] = res.data;
        });
        setForecasts(forecastsMap);
      } catch (err) {
        setForecastError(err.message);
      } finally {
        setLoadingForecasts(false);
      }
    };

    fetchAllForecasts();
  }, [forecastableDates]);

  if (loadingSchedules || loadingForecasts) {
    return <FullScreenLoader />;
  }

  if (schedulesError || forecastError) {
    return (
      <PageWrapper className="weather-page-wrapper">
        <div className="weather-error-container">
          <p className="weather-heading">{context.t("weather-not-available")}</p>
          <p>{schedulesError || forecastError}</p>
        </div>
      </PageWrapper>
    );
  }

  if (activeDates.length === 0) {
    return (
      <PageWrapper className="weather-page-wrapper">
        <div className="weather-error-container">
          <p className="weather-heading">{context.t("weather-not-available")}</p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="weather-page-wrapper">
      <div className="weather-header-container">
        <div className="weather-title-section">
          <ThemedHeading className="weather-heading">{context.t("weather_forecast")}</ThemedHeading>
        </div>
      </div>

      <div className="weather-card-list">
        {forecastableDates.map((schedule, index) => (
          <WeatherForecastInformation
            key={schedule.id}
            schedule={schedule}
            forecastData={forecasts[schedule.id]}
            isToday={index === 0}
          />
        ))}
      </div>

      {activeDates.length > MAX_FORECAST_DAYS && (
        <div className="weather-future-message-container">
          {context.t("weather_future_destinations_soon")}
        </div>
      )}
    </PageWrapper>
  );
};

WeatherForecastOverview.contextTypes = {
  t: PropTypes.func.isRequired
};

export default WeatherForecastOverview;
